import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { isBefore, isAfter, isEqual, format, add } from 'date-fns';

import { Flex, Text, useToggle } from '@lucidhq/lucidium';
import { Form } from '@lucidhq/lucidium/components/FormV2';
import { useFeatureFlag } from '@lucidhq/ui-feature-flags/useFeatureFlag';

import { usePermissions } from '~/components/Permissions/PermissionsProvider';
import { ErrorDialog } from '~/components/Shared/ErrorDialog';
import { FormProps } from '~/components/Shared/FormProps';
import { useUpsertDraftCampaign } from '~/components/Shared/hooks/useMutateCampaignDetails';
import { IM_FLAGS } from '~/impact-measurement/constants';
import { useSplit } from '~/impact-measurement/hooks';
import { Campaign, CampaignStatus, CampaignTypes, Channels } from '~/types';
import { getCampaignManagerRoute } from '~/utils/campaignManager';
import { t } from '~/utils/i18n';

import { BackToStudiesDialog } from '../../BackToStudiesDialog';
import { CampaignManagerFooter } from '../../CampaignManagerFooter';
import { useCampaignManager } from '../../CampaignManagerProvider';
import { ProjectManagerField } from '../../ServiceCampaignDetailsStep/Form/ProjectManagerField';
import { AccountField } from './AccountField';
import { CampaignNameField } from './CampaignNameField';
import { CountryLanguageField } from './CountryLanguageField';
import { EndDateField } from './EndDateField';
import { ForcedExposureSection } from './ForcedExposureSection/ForcedExposureSection';
import { IndustryField } from './IndustryField';
import { MEDIA_TYPE, MediaSelectField } from './MediaSelectField';
import { SalesforceField } from './SalesforceField';
import { StartDateField } from './StartDateField';
import { TrackedMediaSection } from './TrackedMediaSection';

// @TODO Review data not needed in the form and remove it
const initialValues: Campaign = {
  account_id: undefined,
  campaign_data_updated_at: '',
  campaign_id: '',
  campaign_name: '',
  client_name: '',
  created_at: '',
  country_language_id: 1,
  default_question_name: '',
  expected_impressions_count: {
    digital: 0,
    linear_tv: 0,
    social: 0
  },
  draft_settings: {
    questions: [],
    baseline_questions: [],
    expected_impressions_count: 0,
    expected_impressions_count_linear_tv: 0,
    expected_impressions_count_social: 0,
    data_partners: [],
    social_platforms: [],
    target_audiences: [],
    // Impact Measurement Only
    extra_questions_enabled: false,
    forced_exposure: {
      creatives_number: 0,
      exposed_sample_size: 0
    }
  },
  // for social platforms only and its optional
  social_configuration: {
    brand_name: '',
    // handles: [],
    ads_start_date: '',
    ads_end_date: ''
  },
  end_date: '',
  errors: null,
  industry_id: -1,
  permission_id: 0,
  start_date: '',
  salesforce_id: '',
  status: CampaignStatus.DRAFT,
  updated_at: '',
  channels: [],
  project_manager_user_id: 0
};

type GetInitValues = {
  managedCampaign?: Campaign;
  isOn: boolean;
};

//NOTE: once flag is turned on for all users we can remove the following logic
const getInitValues = ({ managedCampaign, isOn }: GetInitValues) => {
  const initValues = {
    ...initialValues,
    draft_settings: {
      ...initialValues.draft_settings,
      ...(isOn && {
        expected_media_channels: null
      })
    }
  };

  return !!managedCampaign ? { ...managedCampaign } : initValues;
};

export enum FIELD_NAMES {
  COUNTRY_LANGUAGE = 'country_language_id',
  CAMPAIGN_NAME = 'campaign_name',
  INDUSTRY = 'industry_id',
  START_DATE = 'start_date',
  END_DATE = 'end_date',
  DATA_PARTNERS = 'data_partners'
}

enum MIN_IMPRESSIONS {
  DIGITAL = 1_000_000,
  LINEAR_TV = 20_000_000,
  SOCIAL = 300_000
}
const MIN_RESPONDENTS_PER_CREATIVE = 200;
const MAX_RESPONDENTS_PER_CREATIVE = 1000;

const handleValidate = (
  values: Campaign,
  isAdmin: boolean,
  initialValues?: Campaign,
  isForcedExposure = false,
  treatment = 'off'
) => {
  const errors: Record<string, any> = {};
  const draft_settings: Record<string, any> = {};
  const expected_impressions_count: Record<string, any> = {};

  const isDigitalChecked = values.channels.includes(Channels.DIGITAL);
  const isLinearTVChecked = values.channels.includes(Channels.LINEAR_TV);
  const isSocialChecked = values.channels.includes(Channels.SOCIAL);
  // NOTE: As of this PR the value mappings are US-Eng:1 UK-Eng:2 AUS-Eng:4 and CAN-Eng:5 in production
  // If there are issues in a dev environment it is because the ids are different in dev and will need to be handled on a case by case basis for now.
  const acceptedCountryLangs = [1, 2, 4, 5];
  const isValidCountryLang = acceptedCountryLangs.includes(
    values.country_language_id
  );

  if (!values.campaign_name) {
    errors.campaign_name = t('forms.errors.requiredField');
  }

  if (!values.start_date) {
    errors.start_date = t('forms.errors.requiredField');
  }

  if (!values.end_date) {
    errors.end_date = t('forms.errors.requiredField');
  }

  if (!!values?.start_date && !!values?.end_date) {
    // This adjustment is mainly to check only the day value and validate the difference between dates.
    const adjustedStartDate = add(new Date(values?.start_date), { days: 1 });
    const startDate = new Date(format(adjustedStartDate, 'MM/dd/yyyy'));
    const endDate = new Date(format(new Date(values.end_date), 'MM/dd/yyyy'));

    if (isAfter(startDate, endDate)) {
      errors.start_date = t('forms.errors.startDate');
    }

    if (isBefore(endDate, startDate)) {
      errors.end_date = t('forms.errors.endDate');
    }

    if (isEqual(startDate, endDate)) {
      errors.start_date = t('forms.errors.startDate');
      errors.end_date = t('forms.errors.endDate');
    }

    if (isBefore(endDate, new Date())) {
      errors.end_date = t('forms.errors.endDatePast');
    }
  }

  if (isAdmin && !values.permission_id) {
    errors.permission_id = t('forms.errors.requiredField');
  }

  if (values.industry_id < 0) {
    errors.industry_id = t('forms.errors.requiredField');
  }
  // NOTE: if linear tv is checked and country language is not US or UK english
  // Also, we do not support a country_lang_id that is set to 0.

  if (!isForcedExposure) {
    if (isLinearTVChecked && values.country_language_id > 2) {
      errors.country_language_id = t('forms.errors.countryLanguage');
    }

    if (isSocialChecked && !isValidCountryLang) {
      errors.country_language_id = t('forms.errors.countryLanguageSocial');
    }

    if (!values.channels.length) {
      errors.channels = t('forms.errors.channels');
    }

    // if channels includes digital and does not have any impressions show error
    if (
      isDigitalChecked &&
      (values?.expected_impressions_count?.digital ?? 0) <
        MIN_IMPRESSIONS.DIGITAL
    ) {
      expected_impressions_count.digital = t('forms.errors.digitalImpressions');
    }

    if (
      isDigitalChecked &&
      initialValues &&
      (values?.expected_impressions_count?.digital ?? 0) <
        (initialValues?.expected_impressions_count?.digital ?? 0)
    ) {
      expected_impressions_count.digital = t(
        'forms.errors.smallerThanInitial',
        {
          min: initialValues?.expected_impressions_count?.digital.toLocaleString()
        }
      );
    }

    if (
      isDigitalChecked &&
      values?.draft_settings?.expected_media_channels === null
    ) {
      draft_settings.expected_media_channels = t('forms.errors.requiredField');
    }

    // if channels includes tv and does include any tv impressions show error
    if (
      isLinearTVChecked &&
      (values?.expected_impressions_count?.linear_tv ?? 0) <
        MIN_IMPRESSIONS.LINEAR_TV
    ) {
      expected_impressions_count.linear_tv = t('forms.errors.tvImpressions');
    }

    if (
      isLinearTVChecked &&
      initialValues &&
      (values?.expected_impressions_count?.linear_tv ?? 0) <
        (initialValues?.expected_impressions_count?.linear_tv ?? 0)
    ) {
      expected_impressions_count.linear_tv = t(
        'forms.errors.smallerThanInitial',
        {
          min: initialValues?.expected_impressions_count?.linear_tv.toLocaleString()
        }
      );
    }

    if (isLinearTVChecked && !values?.draft_settings?.data_partners?.length) {
      draft_settings.data_partners = t('forms.errors.requiredField');
    }

    // Social
    if (isSocialChecked) {
      const socialConfigErrors: Record<string, any> = {};

      if (
        (values?.expected_impressions_count?.social ?? 0) <
        MIN_IMPRESSIONS.SOCIAL
      ) {
        expected_impressions_count.social = t('forms.errors.socialImpressions');
      }

      if (
        initialValues &&
        (values?.expected_impressions_count?.social ?? 0) <
          (initialValues?.expected_impressions_count?.social ?? 0)
      ) {
        expected_impressions_count.social = t(
          'forms.errors.smallerThanInitial',
          {
            min: initialValues?.expected_impressions_count?.social.toLocaleString()
          }
        );
      }

      if (values?.draft_settings?.social_platforms?.length < 1) {
        draft_settings.social_platforms = t('forms.errors.minSocialPlatforms');
      }

      if (values?.draft_settings?.social_platforms?.length > 5) {
        draft_settings.social_platforms = t('forms.errors.maxSocialPlatforms');
      }
      if (treatment === 'on') {
        if (values?.social_configuration?.brand_name.trim() === '') {
          socialConfigErrors.brand_name = t('forms.errors.socialBrandName');
        }
        if (values?.social_configuration?.ads_start_date.trim() === '') {
          socialConfigErrors.ads_start_date = t('forms.errors.requiredField');
        }

        if (values?.social_configuration?.ads_end_date.trim() === '') {
          socialConfigErrors.ads_end_date = t('forms.errors.requiredField');
        }
        if (
          !!values?.social_configuration?.ads_start_date &&
          !!values?.social_configuration?.ads_end_date
        ) {
          const adjustedStartDate = add(
            new Date(values?.social_configuration?.ads_start_date),
            { days: 1 }
          );
          const startDate = new Date(format(adjustedStartDate, 'MM/dd/yyyy'));
          const endDate = new Date(
            format(
              new Date(values?.social_configuration?.ads_end_date),
              'MM/dd/yyyy'
            )
          );

          if (isAfter(startDate, endDate)) {
            socialConfigErrors.ads_start_date = t('forms.errors.startDate');
          }

          if (isBefore(endDate, startDate)) {
            socialConfigErrors.ads_end_date = t('forms.errors.endDate');
          }

          if (isEqual(startDate, endDate)) {
            socialConfigErrors.ads_start_date = t('forms.errors.startDate');
            socialConfigErrors.ads_end_date = t('forms.errors.endDate');
          }

          if (isBefore(endDate, new Date())) {
            socialConfigErrors.ads_end_date = t('forms.errors.endDatePast');
          }
        }
        if (Object.keys(socialConfigErrors).length > 0) {
          if (!errors.social_configuration) {
            errors.social_configuration = {};
          }
          Object.assign(errors.social_configuration, socialConfigErrors);
        }
      }
    }
  } else {
    const creativesNumber =
      values?.draft_settings?.forced_exposure?.creatives_number;
    const sampleSize =
      values?.draft_settings?.forced_exposure?.exposed_sample_size;

    if (!isValidCountryLang) {
      errors.country_language_id = t(
        'forms.errors.countryLanguageForcedExposure'
      );
    }

    if (!creativesNumber) {
      draft_settings.forced_exposure ??= {};
      draft_settings.forced_exposure.creatives_number = t(
        'forms.errors.requiredField'
      );
    }

    if (!sampleSize || sampleSize < MIN_RESPONDENTS_PER_CREATIVE) {
      draft_settings.forced_exposure ??= {};
      draft_settings.forced_exposure.exposed_sample_size = t(
        'forms.errors.minRespondentsPerCreative'
      );
    } else if (sampleSize > MAX_RESPONDENTS_PER_CREATIVE) {
      draft_settings.forced_exposure ??= {};
      draft_settings.forced_exposure.exposed_sample_size = t(
        'forms.errors.maxRespondentsPerCreative'
      );
    }
  }
  return {
    ...errors,
    ...(Object.keys(draft_settings).length && { draft_settings }),
    ...(Object.keys(expected_impressions_count).length && {
      expected_impressions_count
    })
  };
};

export const CampaignDetailsForm = () => {
  const [isOpen, onToggle] = useToggle();
  const [isBackToStudiesDialogOpen, setIsBackToStudiesDialogOpen] = useState(
    false
  );

  const history = useHistory();
  const {
    goToStep,
    steps,
    activeStep,
    managedCampaign,
    refetchCampaign,
    can,
    isForcedExposure
  } = useCampaignManager();
  const { isAdmin } = usePermissions();
  const { isOn } = useSplit(IM_FLAGS.CTV_TOGGLE);
  const initValues = getInitValues({
    managedCampaign: managedCampaign?.details,
    isOn
  });
  const { treatment } = useFeatureFlag(IM_FLAGS.FF_IM_EXTRA_SOCIAL_FIELDS);

  const [selectedMedia, setSelectedMedia] = useState(
    isForcedExposure ? MEDIA_TYPE.FORCED_EXPOSURE : MEDIA_TYPE.TRACKED_MEDIA
  );

  const isForcedExposureSelected = selectedMedia === MEDIA_TYPE.FORCED_EXPOSURE;

  const { mutate, isLoading } = useUpsertDraftCampaign(
    managedCampaign?.details?.campaign_id
  );

  const hasCampaign = managedCampaign?.details?.campaign_id;

  const handleSubmit = (action: 'close' | 'continue') => (values: Campaign) => {
    // @TODO @ccv2 Remove this logic after campaign v1 is deprecated

    const sendSocial =
      values.channels.includes(Channels.SOCIAL) && treatment === 'on';

    const formattedValues = {
      ...values,
      channels: isForcedExposureSelected
        ? [Channels.FORCED_EXPOSURE]
        : values.channels.filter(
            channel => channel !== Channels.FORCED_EXPOSURE
          ),
      draft_settings: hasCampaign
        ? { ...values.draft_settings }
        : {
            ...values.draft_settings,
            campaign_version: 2
          },
      social_configuration: sendSocial ? values.social_configuration : undefined
    };

    mutate(formattedValues, {
      onSuccess: data => {
        if (!hasCampaign) {
          if (action === 'close') {
            setIsBackToStudiesDialogOpen(true);
          }
          if (action === 'continue') {
            history.push(
              getCampaignManagerRoute(
                CampaignTypes.SOFTWARE,
                data.campaign_id,
                steps.MEDIA_TRACKING.id
              )
            );
          }
        } else {
          if (action === 'close') {
            setIsBackToStudiesDialogOpen(true);
          }
          if (action === 'continue') {
            refetchCampaign();
            const nextStep = can.limitedEdit
              ? steps.REVIEW.id
              : steps[activeStep].nextId;
            if (nextStep) goToStep(nextStep);
          }
        }
      },
      onError: () => {
        onToggle();
      }
    });
  };

  return (
    <>
      <BackToStudiesDialog
        isOpen={isBackToStudiesDialogOpen}
        onOpenChange={setIsBackToStudiesDialogOpen}
      />
      <Form
        initialValues={initValues}
        validate={values =>
          handleValidate(
            values,
            isAdmin,
            can.limitedEdit ? initValues : undefined,
            isForcedExposureSelected,
            treatment
          )
        }
        // @ts-ignore
        onSubmit={() => null}
        validateOnBlur={false}
        validateOnChange={true}
        enableReinitialize
      >
        <CampaignNameField
          readOnly={!!hasCampaign && !(can.edit || can.limitedEdit)}
        />

        <Text
          lss={{ my: 'md', fontWeight: 'bold', fontSize: '1rem' }}
          as="strong"
        >
          {t(`campaignManager.campaignDetails.questions.dates`)}
        </Text>

        <Flex lss={{ justifyContent: 'space-between', my: 'md', mt: 'xl' }}>
          <StartDateField
            name={FIELD_NAMES.START_DATE}
            readOnly={!!hasCampaign && !can.edit}
          />
          <EndDateField
            name={FIELD_NAMES.END_DATE}
            readOnly={!!hasCampaign && !(can.edit || can.limitedEdit)}
          />
        </Flex>

        {isAdmin && (
          <Flex lss={{ flexDirection: 'column', gap: '0.5rem' }}>
            <AccountField readOnly={!!hasCampaign && !can.edit} isRequired />

            <ProjectManagerField readOnly={!!hasCampaign && !can.edit} />

            <SalesforceField
              readOnly={!!hasCampaign && !(can.edit || can.limitedEdit)}
            />
          </Flex>
        )}

        <CountryLanguageField
          name={FIELD_NAMES.COUNTRY_LANGUAGE}
          readOnly={!!hasCampaign && !can.edit}
        />

        <IndustryField
          name={FIELD_NAMES.INDUSTRY}
          readOnly={!!hasCampaign && !can.edit}
        />

        <MediaSelectField value={selectedMedia} setValue={setSelectedMedia} />

        {selectedMedia === MEDIA_TYPE.FORCED_EXPOSURE ? (
          <ForcedExposureSection />
        ) : (
          <TrackedMediaSection limitedEdit={!!hasCampaign && can.limitedEdit} />
        )}

        <FormProps>
          {({ validateForm, values }) => (
            <CampaignManagerFooter
              onBack={() => setIsBackToStudiesDialogOpen(true)}
              saveCloseProps={{
                onClick: async () => {
                  const errors = await validateForm();
                  if (Object.values(errors).length) return;
                  const formHandler = handleSubmit('close');
                  formHandler(values as Campaign);
                },
                type: 'button',
                isDisabled: isLoading
              }}
              saveContinueProps={{
                onClick: async () => {
                  const errors = await validateForm();
                  if (Object.values(errors).length) return;
                  const formHandler = handleSubmit('continue');
                  formHandler(values as Campaign);
                },
                isDisabled: isLoading,
                type: 'submit'
              }}
            />
          )}
        </FormProps>
      </Form>
      <ErrorDialog
        isOpen={isOpen}
        onClose={onToggle}
        errorText={t('errors.campaignDraft')}
      />
    </>
  );
};

/**
 * Converts a comma-separated string into an array of strings.
 * It also trims whitespace and removes empty entries.
 */
// function csvToArray(csvString: string | null | undefined): string[] {
//   if (!csvString) {
//     return [];
//   }
//   return csvString
//     .split(',')
//     .map(item => item.trim()) // Remove whitespace
//     .filter(item => item.length > 0); // Remove empty strings
// }
